import { queryClient } from "./queryClient"

function getCsrfToken(): string | null {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta ? meta.getAttribute("content") : null
}

async function request<T>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...(options.headers as Record<string, string>),
  }

  const csrfToken = getCsrfToken()
  if (csrfToken) {
    headers["X-CSRF-Token"] = csrfToken
  }

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: "same-origin",
  })

  if (response.status === 204) {
    return undefined as T
  }

  const data = await response.json()

  if (!response.ok) {
    if (response.status === 401) {
      // Store error message for the login page (skip login POST attempts
      // which show errors inline via mutation state).
      if (data.error && options.method !== "POST" && !url.endsWith("/auth/me")) {
        sessionStorage.setItem("auth_error", data.error)
      }

      // If any non-auth API call returns 401, clear auth state so the
      // layout auth guards redirect to login via client-side navigation
      // (avoids a full-page reload that loses React state).
      if (!url.includes("/auth/")) {
        queryClient.resetQueries({ queryKey: ["currentUser"] })
        queryClient.resetQueries({ queryKey: ["currentAdmin"] })
      }
    }

    const error = new ApiError(
      data.error || "An error occurred",
      response.status,
      data
    )
    throw error
  }

  return data as T
}

export class ApiError extends Error {
  status: number
  data: unknown

  constructor(message: string, status: number, data: unknown) {
    super(message)
    this.name = "ApiError"
    this.status = status
    this.data = data
  }
}

export const api = {
  get: <T>(url: string) => request<T>(url),
  post: <T>(url: string, body?: unknown) =>
    request<T>(url, { method: "POST", body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(url: string, body?: unknown) =>
    request<T>(url, { method: "PATCH", body: body ? JSON.stringify(body) : undefined }),
  put: <T>(url: string, body?: unknown) =>
    request<T>(url, { method: "PUT", body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(url: string) => request<T>(url, { method: "DELETE" }),
}
